import {
    ApprovalPurchaseOrderKind,
    Decimal,
    PurchaseOrderGet,
    PurchaseOrderStatus,
    Ulid,
} from "@kase/my-marathon-communication";
import { ReadonlySignal, computed, signal } from "@preact/signals";
import { waitForSignal } from "~/utils";
import { ApiSliceParams } from "../interfaces";
import {
    createListController,
    deferDocument,
    getIdWithCompany,
} from "../utils";
import {
    PurchaseOrder,
    PurchaseOrderItemLine,
    PurchaseOrderLine,
    PurchaseOrderLineItemInput,
    PurchaseOrderPrintRequest,
    PurchaseOrderSendExternalRequest,
    PurchaseOrderSendInternalRequest,
    PurchaseOrderTextLine,
    PurchaseOrderUpdateInput,
} from "./interfaces";

export const makePurchase = ({ connection, company }: ApiSliceParams) => {
    const ordersStore: Record<string, ReadonlySignal<PurchaseOrder[]>> = {};
    const previewState: Record<string, boolean> = {};

    const openOrder = async (order: PurchaseOrder) => {
        const con = connection.value;

        if (!con) {
            return;
        }

        order.loading.value = true;

        const lines: PurchaseOrderLine[] = [];

        for await (const id of con.purchaseorder.lines.list({
            company: order.company,
            po: order._id,
        })) {
            for await (const item of con.purchaseorder.lines.get({
                company: order.company,
                id,
            })) {
                lines.push({
                    ...item,
                    id: item.id.toString(),
                    _id: item.id,
                });
            }
        }

        order.lines.value = lines;
        order.loading.value = false;
    };

    const createItemLine = async (
        order: PurchaseOrder,
        item: Partial<PurchaseOrderLineItemInput> = {},
    ) => {
        const con = connection.value;

        await con.purchaseorder.lines.createItem({
            company: order.company,
            po: order._id,
            item: {
                name: item.name ?? "",
                description: item.description ?? "",
                quantity: item.quantity ?? new Decimal(0),
                unit_price: item.unit_price ?? new Decimal(0),
                unit: item.unit ?? "EA",
            },
        });

        await openOrder(order);
    };

    const updateItemLine = async (
        order: PurchaseOrder,
        line: PurchaseOrderItemLine,
        params: Partial<PurchaseOrderLineItemInput>,
    ) => {
        const con = connection.value;

        await con.purchaseorder.lines.updateItem({
            company: order.company,
            id: line._id,
            etag: line.etag,
            ...params,
        });

        await openOrder(order);
    };

    const removeLine = async (
        order: PurchaseOrder,
        line: PurchaseOrderLine,
    ) => {
        const con = connection.value;
        const req = {
            company: order.company,
            id: line._id,
            etag: line.etag,
        };

        if (line.line_type?.$case === "text") {
            await con.purchaseorder.lines.deleteText(req);
        } else {
            await con.purchaseorder.lines.deleteItem(req);
        }

        await openOrder(order);
    };

    const view = (order: PurchaseOrderGet, cmp: string) => {
        const lines = signal<PurchaseOrderLine[]>([]);

        const finalOrder: PurchaseOrder = {
            ...order,
            id: getIdWithCompany(order.id, cmp),
            _id: order.id,
            loading: signal(false),
            company: cmp,
            lines,
            document: null,
            documentTag: signal(0),
            items: computed(() =>
                lines.value.filter(
                    (line): line is PurchaseOrderItemLine =>
                        line.line_type?.$case === "item",
                ),
            ),
            texts: computed(() =>
                lines.value.filter(
                    (line): line is PurchaseOrderTextLine =>
                        line.line_type?.$case === "text",
                ),
            ),
        };

        return finalOrder;
    };

    const {
        items: orders,
        allLoading: loading,
        allLoaded: loaded,
        listen: listenCompany,
    } = createListController<PurchaseOrderGet, PurchaseOrder, string>({
        name: "purchaseorder",
        connection,
        list: (con, company) => con.purchaseorder.items({ company }),
        getId: (order) => getIdWithCompany(order.id, company.value),
        getKey: (company) => company,
        view,
    });

    const listen = () => {
        listenCompany(company.value);
    };

    const open = (id: string) => {
        if (previewState[id]) {
            return;
        }

        previewState[id] = true;

        const order = orders.peek().find((o) => o.id === id);

        if (order) {
            order.document = deferDocument(
                connection.value.purchaseorder.getDocument({
                    company: order.company,
                    id: order._id,
                }),
            );
            order.documentTag.value = order.documentTag.peek() + 1;
        }
    };

    const getDocument = (order: Readonly<PurchaseOrder>) =>
        connection.value.purchaseorder.getDocument({
            company: order.company,
            id: order._id,
        });

    const doc = (id: string | null | undefined) => {
        if (!id) {
            return null;
        }

        const item = orders.value.find((o) => o.id === id);

        if (item) {
            void item.documentTag.value;
        }

        return item?.document ?? null;
    };

    const projectOrders = (id: string) => {
        if (!(id in ordersStore)) {
            ordersStore[id] = computed(() =>
                orders.value.filter(
                    (order) => order.project?.project.toString() === id,
                ),
            );
        }

        if (!loading.peek() && !loaded.peek()) {
            listen();
        }

        return ordersStore[id];
    };

    const get = (
        id: string | Readonly<Ulid> | null | undefined,
        cmp = company.value,
    ) => {
        if (!id) {
            return null;
        }

        const key = getIdWithCompany(id, cmp);

        return orders.value.find((s) => s.id === key) ?? null;
    };

    const create = (
        req: Parameters<typeof connection.value.purchaseorder.create>[0],
    ) => connection.value.purchaseorder.create(req);

    const print = (
        id: Readonly<Ulid>,
        params: PurchaseOrderPrintRequest = {},
    ) =>
        connection.value.purchaseorder.print({
            company: params.company ?? company.value,
            id,
            save: params.save ?? false,
            show_item_ids: params.showItemIds ?? false,
            show_prices: params.showPrices ?? false,
            show_vat: params.showVat ?? false,
        });

    const sendInternal = async ({
        id,
        approver,
    }: PurchaseOrderSendInternalRequest) => {
        await connection.value.purchaseorder.update({
            company: company.value,
            id,
            status: PurchaseOrderStatus.PurchaseOrderStatusInternal,
        });
        await connection.value.approve.purchaseorder.approval.create({
            purchaseorder: id,
            approver,
            approval_kind: ApprovalPurchaseOrderKind.PurchaseOrderKindInternal,
            company: company.value,
        });
    };

    const sendExternal = async ({
        id,
        email,
    }: PurchaseOrderSendExternalRequest) => {
        await connection.value.purchaseorder.update({
            company: company.value,
            id,
            status: PurchaseOrderStatus.PurchaseOrderStatusExternal,
        });
        await connection.value.approve.purchaseorder.approval.create({
            purchaseorder: id,
            email,
            approval_kind: ApprovalPurchaseOrderKind.PurchaseOrderKindExternal,
            company: company.value,
        });
    };

    const cloneProject = async (source: string, target: string) => {
        if (source === target) {
            return;
        }

        if (!loading.peek() && !loaded.peek()) {
            listen();
        }

        await waitForSignal(loading, false);

        const sourceOrders = orders
            .peek()
            .filter((order) => order.project?.project.toString() === source);

        for (const order of sourceOrders) {
            await openOrder(order);

            const lines = order.items.peek().map((line) => ({
                line_type: {
                    $case: "item" as const,
                    item: {
                        name: line.line_type.item.name,
                        description: line.line_type.item.description,
                        quantity: line.line_type.item.quantity,
                        unit_price: line.line_type.item.unit_price,
                        unit: line.line_type.item.unit,
                    },
                },
            }));

            await create({
                company: company.value,
                kam_company: order.kam_company,
                our_ref: order.our_ref,
                your_ref: order.your_ref ?? "",
                supplier_id: order.supplier_id,
                project: { project: Ulid.fromString(target) },
                client_name: order.client_name ?? "",
                delivery_address1: order.delivery_address1 ?? "",
                delivery_address2: order.delivery_address2 ?? "",
                header: order.header ?? "",
                purchase_price: order.purchase_price,
                lines,
            });
        }
    };

    const updateOrder = (
        order: PurchaseOrder,
        params: PurchaseOrderUpdateInput,
    ) =>
        connection.value.purchaseorder.update({
            company: order.company,
            id: order._id,
            ...params,
        });

    const dec2num = (value: Parameters<typeof Decimal.from>[0] | undefined) =>
        value ? new Decimal(value).toNumber() : 0;

    const sumItems = (order: PurchaseOrder) =>
        order.items
            .peek()
            .reduce(
                (sum, line) =>
                    sum +
                    dec2num(line.line_type.item.quantity) *
                        dec2num(line.line_type.item.unit_price),
                0,
            );

    const moveLine = async (
        source: PurchaseOrder,
        target: PurchaseOrder,
        line: PurchaseOrderItemLine,
    ) => {
        await createItemLine(target, {
            name: line.line_type.item.name,
            description: line.line_type.item.description,
            quantity: new Decimal(line.line_type.item.quantity ?? Decimal.ZERO),
            unit_price: new Decimal(
                line.line_type.item.unit_price ?? Decimal.ZERO,
            ),
            unit: line.line_type.item.unit,
        });
        await removeLine(source, line);
        await Promise.all([
            updateOrder(source, { purchase_price: sumItems(source) }),
            updateOrder(target, { purchase_price: sumItems(target) }),
        ]);
    };

    return {
        orders,
        loading,
        projectOrders,
        listen,
        listenCompany,
        open,
        doc,
        getDocument,
        get,
        create,
        cloneProject,
        print,
        sendInternal,
        sendExternal,
        updateOrder,
        moveLine,
        openOrder,
        createItemLine,
        updateItemLine,
        removeLine,
    };
};
